import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppBreadcrumbs } from '../../components/navigation/AppBreadcrumbs';
import { brandColors } from '../../theme';
import { AppLink } from '../../components/navigation/AppLink';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { SEO } from '../../components/SEO';
import { generateBreadcrumbSchema } from '../../components/SEO/schemas';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  useContinents,
  useMarketsByCountrySlug,
  usePropertySearchByCountry,
  type Market,
  type PropertySearchResult,
} from '../../hooks/useMarketCompassApi';

const createSlug = (name: string): string => {
  return name.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const CountryMarkets: React.FC = () => {
  const navigate = useNavigate();
  const { hasStorePrivileged } = useUserPermissions();
  const { 
    continentName, 
    countryName, 
  } = useParams<{
    continentName: string;
    countryName: string;
  }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const location = useLocation();

  // Initialize search from query parameter "q"
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setSearchQuery(q);
    }
  }, [location.search]);

  // Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch real data from API
  const { data: continents = [], isLoading: loadingContinents } = useContinents();

  const selectedContinent = useMemo(() => {
    if (!continentName || continents.length === 0) {
      return undefined;
    }
    return continents.find(c => createSlug(c.name) === continentName);
  }, [continentName, continents]);

  // Use new endpoint that returns markets + country metadata in one call
  const {
    data: marketData,
    isLoading: loadingMarkets,
    error: marketsError
  } = useMarketsByCountrySlug(continentName, countryName);

  const markets = marketData?.markets || [];
  const selectedCountry = marketData?.country;
  
  // Filter markets by search query and active status
  const filteredMarkets = markets.filter(market => {
    // Filter out inactive markets for users without store.privileged permission
    if (!hasStorePrivileged && market.isActive === false) {
      return false;
    }
    // Apply search filter
    return market.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Property search (only fires when debounced query is 2+ chars)
  const {
    data: propertyResults = [],
    isFetching: fetchingProperties,
  } = usePropertySearchByCountry(
    continentName,
    countryName,
    debouncedSearchQuery,
    { limit: 50 }
  );

  const showPropertyResults = debouncedSearchQuery.trim().length >= 2;

  // Check if there are any inactive markets being displayed
  const hasInactiveMarkets = filteredMarkets.some(m => m.isActive === false);

  // Handle market selection
  const handleMarketSelect = (market: Market) => {
    navigate(`/global-market-reports/${createSlug(selectedContinent!.name)}/${createSlug(selectedCountry!.name)}/${market.slug}`);
  };

  // Handle loading states
  if (loadingContinents) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle not found state
  if (!selectedContinent) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Continent not found. <AppLink to='/global-market-reports'>Go back to continents</AppLink>
        </Alert>
      </Container>
    );
  }

  // Handle loading state
  if (loadingMarkets) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading markets...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Handle country not found (only if we're done loading and still no data)
  if (!loadingMarkets && !marketData && !selectedCountry) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Country not found. <AppLink to='/global-market-reports'>Go back to continents</AppLink>
        </Alert>
      </Container>
    );
  }

  // If selectedCountry isn't available yet but we have marketData, continue rendering
  // This prevents showing error state during a brief data derivation moment
  if (!selectedCountry) {
    return <></>;
  }

  // Handle error state
  if (marketsError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Failed to load markets for {selectedCountry.name}. Please try again.
        </Alert>
      </Container>
    );
  }

  // Handle empty markets state
  if (markets.length === 0) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info" sx={{ my: 4 }}>
          No markets available for {selectedCountry.name} at this time.
        </Alert>
      </Container>
    );
  }

  // Generate breadcrumb schema
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Global Market Reports', url: '/global-market-reports' },
    { name: marketData?.continent.name || continentName?.replace(/-/g, ' ') || '', url: `/global-market-reports/${continentName}` },
    { name: selectedCountry.name, url: `/global-market-reports/${continentName}/${countryName}` }
  ]);

  return (
    <>
      <SEO
        title={`${selectedCountry.name} Hotel Market Reports - All Cities & Regions`}
        description={`Browse all ${selectedCountry.activeMarketCount} hospitality market reports for ${selectedCountry.name}. Get detailed hotel analytics, ADR, RevPAR, occupancy rates, and performance metrics for each city and region.`}
        canonical={`/global-market-reports/${continentName}/${countryName}`}
        structuredData={breadcrumbSchema}
      />
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography
            component="h1"
            sx={{
              color: brandColors.blue,
              fontWeight: 400,
              fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
              lineHeight: 1.05,
              letterSpacing: '-0.5px',
              mb: 2,
            }}
          >
            {selectedCountry.name} Markets
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {hasStorePrivileged ? selectedCountry.marketCount : selectedCountry.activeMarketCount} markets available
          </Typography>
        </Box>

      {/* Breadcrumbs */}
      <AppBreadcrumbs
        items={[
          { label: 'Continents', path: '/global-market-reports' },
          { label: selectedContinent.name, path: `/global-market-reports/${createSlug(selectedContinent.name)}` },
          { label: selectedCountry.name }
        ]}
        sx={{ mb: 3 }}
      />

      {/* Show warning for inactive markets when viewed by special users */}
      {hasInactiveMarkets && hasStorePrivileged && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Special Access View
          </Typography>
          <Typography variant="body2">
            Some markets shown are flagged as Internal Only which means they are only visible to Horwath HTL licensees and not regular users.
          </Typography>
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder={`Search markets or properties in ${selectedCountry.name}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: brandColors.blue }} />
            <Typography variant="caption" sx={{ color: brandColors.blue, fontWeight: 600 }}>
              Search by market name, property name, or city
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* No Results State */}
      {filteredMarkets.length === 0 ? (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Alert severity="info">
            No markets found matching "{searchQuery}"
          </Alert>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
              {filteredMarkets.map((market) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={market.id}>
                  <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        border: `1px solid ${brandColors.lightGray}`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          borderColor: brandColors.blue,
                        },
                      }}
                    >
                      <CardActionArea
                        component={AppLink}
                        to={`/global-market-reports/${createSlug(selectedContinent.name)}/${createSlug(selectedCountry.name)}/${market.slug}`}
                        sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                        }}
                      >
                      {market.isActive === false && (
                        <Chip
                          label="Internal Only"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: '#FFCC00',
                            color: 'black',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            zIndex: 1,
                          }}
                        />
                      )}
                      {/* Product Image */}
                      {market.productImage2 && (
                        <Box
                          component="img"
                          src={market.productImage2}
                          alt={market.name}
                          sx={{
                            width: '100%',
                            height: 180,
                            objectFit: 'cover',
                            borderTopLeftRadius: 4,
                            borderTopRightRadius: 4,
                          }}
                        />
                      )}

                      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{
                            fontWeight: 600,
                            color: brandColors.indigo,
                            mb: 1.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {market.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: brandColors.mediumGray,
                            mb: 0.5,
                          }}
                        >
                          {selectedCountry.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: brandColors.mediumGray,
                          }}
                        >
                          {selectedContinent.name}
                        </Typography>
                      </CardContent>
                      </CardActionArea>

                      <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
                        <Button
                          component={AppLink}
                          to={`/global-market-reports/${createSlug(selectedContinent.name)}/${createSlug(selectedCountry.name)}/${market.slug}`}
                          size="small"
                          variant="outlined"
                          startIcon={<TrendingUpIcon />}
                          sx={{
                            borderColor: brandColors.blue,
                            color: brandColors.blue,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: brandColors.blue,
                              backgroundColor: 'rgba(32, 76, 229, 0.05)',
                            },
                          }}
                        >
                          View Market
                        </Button>
                      </CardActions>
                    </Card>
                </Grid>
              ))}
        </Grid>
      )}

      {/* Property Search Results */}
      {showPropertyResults && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, color: brandColors.blue }}>
            Property Results
            {fetchingProperties && (
              <CircularProgress size={18} sx={{ ml: 1.5, verticalAlign: 'text-bottom' }} />
            )}
          </Typography>

          {!fetchingProperties && propertyResults.length === 0 ? (
            <Alert severity="info">
              No properties found matching "{debouncedSearchQuery}" in {selectedCountry.name}
            </Alert>
          ) : propertyResults.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Property Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rooms</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Submarket</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {propertyResults.map((property) => (
                    <TableRow
                      key={property.id}
                      hover
                      sx={{
                        cursor: property.marketSlug ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: 'rgba(32, 76, 229, 0.04)',
                        },
                      }}
                      onClick={() => {
                        if (property.marketSlug && continentName && countryName) {
                          navigate(
                            `/global-market-reports/${continentName}/${countryName}/${property.marketSlug}`
                          );
                        }
                      }}
                    >
                      <TableCell>{property.propertyName ?? '\u2014'}</TableCell>
                      <TableCell>{property.rooms ?? '\u2014'}</TableCell>
                      <TableCell>{property.city ?? '\u2014'}</TableCell>
                      <TableCell>{property.marketName ?? '\u2014'}</TableCell>
                      <TableCell>{property.submarketName ?? '\u2014'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Box>
      )}
    </Container>
    </>
  );
};

export default CountryMarkets;